<template>
  <div>
    <div class="flex align-items-baseline">
      <h2 class="dashboard-stat-label m-t-none">
        Weight History
      </h2>
      <button v-if="canEdit && !showForm" class="btn btn-default m-l-auto" @click="openAddForm">
        <i class="fa-solid fa-plus m-r-xxs"></i>Log weight
      </button>
    </div>
    <div v-if="optedOut" class="bg-gray-50 rounded-xs p-sm m-b-m text-gray-600">
      <i class="fa-solid fa-circle-info m-r-xxs"></i>
      Weight tracking is turned off for every roster this wrestler is on, so the kiosk won't ask them to weigh in.
      <template v-if="canEdit">Weights can still be logged manually here.</template>
    </div>
    <div v-if="canEdit && showForm" class="bg-gray-50 rounded-xs p-m m-b-m">
      <h3 class="m-t-none font-semi">{{ editingEntry ? 'Edit weight entry' : 'Log weight' }}</h3>
      <div class="flex gap-x-sm flex-wrap-sm-only">
        <div class="form-group">
          <label for="weight_entry_weight" class="m-b-xxs">Weight</label>
          <input
            id="weight_entry_weight"
            v-model="form.weight"
            type="number"
            inputmode="decimal"
            min="0"
            step="0.1"
            class="form-control"
            style="max-width: 140px"
            placeholder="148.2"
          >
        </div>
        <div class="form-group">
          <label for="weight_entry_units" class="m-b-xxs">Units</label>
          <select id="weight_entry_units" v-model="form.units" class="form-control" style="max-width: 100px">
            <option value="lbs">lbs</option>
            <option value="kg">kg</option>
          </select>
        </div>
        <div class="form-group">
          <label for="weight_entry_recorded_at" class="m-b-xxs">Weigh-in time</label>
          <input
            id="weight_entry_recorded_at"
            v-model="form.recordedAt"
            type="datetime-local"
            class="form-control"
          >
        </div>
      </div>
      <div class="flex gap-x-sm align-items-baseline">
        <ladda-button
          @lbClicked="saveEntry"
          el-class="btn-primary"
          :loading="saving"
        >
          Save
        </ladda-button>
        <button class="btn btn-default" @click="closeForm">Cancel</button>
        <small v-if="error" class="text-danger">{{ errorMessage }}</small>
      </div>
    </div>
    <paged-list
      response-key="weight_entries"
      object-type="WeightEntry"
      help-text=""
      empty-text="No weights logged yet"
      :base-url="baseUrl"
    >
      <template v-slot:list="slotProps">
        <div class="flex flex-col border border-b p-y-sm border-solid border-gray-50">
          <div class="flex gap-x-sm align-items-baseline">
            <div>
              <p class="font-normal text-16 m-t-none m-b-none">
                {{ formatWeight(slotProps.result.weight) }} {{ slotProps.result.units }}
              </p>
              <p class="search-type-label m-b-none">
                {{ formatWithMoment(slotProps.result.recorded_at, 'llll') }}
                <template v-if="sourceLabel(slotProps.result.created_via)">
                  &middot; {{ sourceLabel(slotProps.result.created_via) }}
                </template>
              </p>
            </div>
            <div v-if="canEdit" class="m-l-auto flex gap-x-sm">
              <a class="cursor-pointer" @click="openEditForm(slotProps.result)">
                <i class="fa-solid fa-pencil color-gray-400 m-r-xxs"></i>Edit
              </a>
              <a class="cursor-pointer text-red-600" @click="deleteEntry(slotProps.result)">
                <i class="fa-solid fa-trash-can m-r-xxs"></i>Delete
              </a>
            </div>
          </div>
        </div>
      </template>
    </paged-list>
  </div>
</template>
<script>
import PagedList from './paged_list.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { formattingMixin } from './mixins/formatting_mix';
import { errorableMix } from './mixins/errorable_mix';

// Teams rarely mix units, so remember the last choice on this browser
const UNITS_STORAGE_KEY = 'wiq_weight_entry_units';

function loadPreferredUnits() {
  try {
    return localStorage.getItem(UNITS_STORAGE_KEY) === 'kg' ? 'kg' : 'lbs';
  } catch (e) {
    return 'lbs';
  }
}

function savePreferredUnits(units) {
  try {
    localStorage.setItem(UNITS_STORAGE_KEY, units);
  } catch (e) {
    // localStorage unavailable - fine, just don't remember
  }
}

export default {
  name: 'wrestler-weight-list',
  components: {
    PagedList,
    LaddaButton,
  },
  mixins: [formattingMixin, errorableMix],
  props: {
    wrestlerProfileId: {
      type: Number,
      required: true,
    },
    // Coaches log/edit/delete; wrestlers and guardians only view
    canEdit: {
      type: Boolean,
      default: false,
    },
    // Every active roster for this wrestler has weight tracking disabled
    optedOut: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showForm: false,
      editingEntry: null,
      saving: false,
      form: {
        weight: '',
        units: 'lbs',
        recordedAt: '',
      },
      // Bumped after create/update/destroy; changes baseUrl so paged-list reloads
      refreshKey: 0,
    };
  },
  computed: {
    baseUrl() {
      return `${this.$apiService.wrestlerWeightEntriesUrl(this.wrestlerProfileId)}?refresh=${this.refreshKey}`;
    },
  },
  methods: {
    formatWeight(weight) {
      const rounded = Math.round(parseFloat(weight) * 100) / 100;
      return Number.isNaN(rounded) ? weight : rounded;
    },
    sourceLabel(createdVia) {
      switch (createdVia) {
        case 'kiosk_self':
          return 'Kiosk';
        case 'kiosk_admin_override':
          return 'Kiosk (admin override)';
        case 'manual_web':
          return 'Manually entered';
        default:
          return '';
      }
    },
    openAddForm() {
      this.editingEntry = null;
      this.form.weight = '';
      this.form.units = loadPreferredUnits();
      this.form.recordedAt = moment().format('YYYY-MM-DDTHH:mm');
      this.error = false;
      this.showForm = true;
    },
    openEditForm(entry) {
      this.editingEntry = entry;
      this.form.weight = entry.weight;
      this.form.units = entry.units;
      this.form.recordedAt = moment(entry.recorded_at).format('YYYY-MM-DDTHH:mm');
      this.error = false;
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editingEntry = null;
      this.error = false;
    },
    saveEntry() {
      const vm = this;
      if (vm.saving) return;

      const weight = parseFloat(vm.form.weight);
      if (Number.isNaN(weight) || weight <= 0) {
        vm.errorMessage = 'Please enter a valid weight';
        vm.error = true;
        return;
      }

      vm.saving = true;
      const data = {
        weight_entry: {
          weight,
          units: vm.form.units,
          recorded_at: vm.form.recordedAt ? moment(vm.form.recordedAt).toISOString() : null,
        },
      };

      const request = vm.editingEntry
        ? axios.put(vm.$apiService.wrestlerWeightEntryUrl(vm.wrestlerProfileId, vm.editingEntry.id), data)
        : axios.post(vm.$apiService.wrestlerWeightEntriesUrl(vm.wrestlerProfileId), data);

      request
        .then(() => {
          savePreferredUnits(vm.form.units);
          vm.saving = false;
          vm.$notificationManager.emit('show-toast', vm.editingEntry ? 'Weight entry updated' : 'Weight logged', true);
          vm.closeForm();
          vm.refreshKey += 1;
        })
        .catch((error) => {
          vm.saving = false;
          vm.errorMessage = `Error: ${error.toString()}`;
          vm.error = true;
        });
    },
    deleteEntry(entry) {
      const vm = this;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this weight entry? This cannot be undone.')) {
        return;
      }

      axios.delete(vm.$apiService.wrestlerWeightEntryUrl(vm.wrestlerProfileId, entry.id))
        .then(() => {
          vm.$notificationManager.emit('show-toast', 'Weight entry deleted', true);
          vm.refreshKey += 1;
        })
        .catch((error) => {
          vm.$notificationManager.emit('show-toast', `Error deleting weight entry: ${error.toString()}`, false);
        });
    },
  },
};
</script>
